import React, { FC } from "react";

import { BaseComponentProps, DataViewKey, useAuthLogout, useCurrentUser } from "@keepeek/commons";
import { KButton, KSearchInput, useThemeColors } from "@keepeek/refront-components";
import { Box, ButtonBase, Divider, Typography, useTheme } from "@mui/material";
import clsx from "clsx";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { KIcon } from "../../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../../components/layout/adminMenu";
import { KHeaderWidget } from "../../../../models/configuration/components/kHeaderWidget";
import { AutoGuestLogin, Global } from "../../../../models/configuration/global";
import { useConfiguration } from "../../../../providers/config/hooks";
import { useInitConfig } from "../../../../providers/config/hooks/init";
import { useBackdoors } from "../../../../providers/config/hooks/useBackdoors";
import { QUERY_BACKDOOR } from "../../../../providers/config/models";
import { useSearchAutocomplete } from "../../../../providers/searchAutocomplete/hooks";
import { useDidomi } from "../../../../providers/tools/didomi/hooks";
import CurrentUserMenu from "../../../CurrentUserMenu";
import CurrentUserPopup from "../../../CurrentUserMenu/CurrentUserPopup";
import EditableSanitizeHtml from "../../../FrontEdit/EditableSanitizeHtml";
import KLanguageSwitcher from "../../../KLanguageSwitcher";
import { getKLanguageSwitcherStyle } from "../../../KLanguageSwitcher/utils";
import { kHeaderWidgetComponentsAtom } from "../atoms";
import CustomLink from "../CustomLink";
import { useKHeaderWidgetSelect } from "../hooks/useKHeaderWidgetSelect";
import { KHeaderWidgetComponent, KHeaderWidgetComponentContext } from "../models";
import StyledKHeaderWidgetActionsBox, { classes } from "./styles";

type KHeaderWidgetActionsBoxProps = {
  widgetId: KHeaderWidget["id"];
  context: KHeaderWidgetComponentContext;
  dataViewKey: DataViewKey;
} & BaseComponentProps;

const KHeaderWidgetActionsBox: FC<React.PropsWithChildren<KHeaderWidgetActionsBoxProps>> =
  function ({
    widgetId,
    context = KHeaderWidgetComponentContext.All,
    className,
    testId = "KHeaderWidgetActionsBox",
    dataViewKey,
    ...rest
  }) {
    const { t } = useTranslation();
    const configuration = useKHeaderWidgetSelect({ widgetId });
    const components = useRecoilValue(kHeaderWidgetComponentsAtom({ widgetId, context }));
    const { convertColor } = useThemeColors();
    const globalConfiguration = useConfiguration<Global>(CONFIGURATION_SECTION_PATH.GLOBAL);
    const { logout } = useAuthLogout();
    const currentUser = useCurrentUser();
    const theme = useTheme();

    const {
      handleQueryUpdate,
      selectOption,
      autoCompleteVals,
      query,
      loading,
      onSearch,
      kSplitValue,
      kSplitValues,
      setKSplitValue,
    } = useSearchAutocomplete({
      widgetId: "header",
      // JBO: This search is always starting from fresh, it is at the top context so we want it to refresh it all
      // This is true at this date 23/03/23... maybe at some point we will want it to be driven by the admin
      resetSearch: true,
      fields: configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsSearchBar?.fields,
      dataViewKey,
    });

    const conditionForConnectButton =
      (configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsUser?.loginButtonAutoConnect ??
        true) &&
      globalConfiguration?.keycloakConfiguration?.autoGuestLogin ===
        AutoGuestLogin.AutoGuestLogin &&
      (globalConfiguration.keycloakConfiguration.displayConnectWithCredentials ||
        globalConfiguration.keycloakConfiguration.displayConnectWithGuest ||
        (globalConfiguration.keycloakConfiguration.idp || []).length > 0) &&
      currentUser.isPublicVisitor;

    const { displayUserLogoutInHeader } = useBackdoors();
    const { initConfig } = useInitConfig();
    const { needDidomi } = useDidomi();
    const cookieConsentManagement =
      configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsUser?.cookieConsentManagement;
    const displayCookieConsentManagement =
      cookieConsentManagement != undefined ? cookieConsentManagement : true;
    const isEmptyMenu =
      globalConfiguration?.keycloakConfiguration?.autoGuestLogin ===
        AutoGuestLogin.AutoGuestLogin &&
      currentUser.isPublicVisitor &&
      !displayUserLogoutInHeader &&
      !(initConfig && displayCookieConsentManagement && needDidomi);

    return (
      <StyledKHeaderWidgetActionsBox
        className={clsx("KHeaderWidgetActionsBox", className, classes.root)}
        context={context}
        data-testid={testId}
        {...rest}
      >
        {/* Lang Selector */}
        {components.includes(KHeaderWidgetComponent.LangSelector) &&
          context === KHeaderWidgetComponentContext.Burger && (
            <KLanguageSwitcher
              className={classes.langSelector}
              sx={getKLanguageSwitcherStyle(
                convertColor(
                  configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsLangSelector?.color,
                ),
              )}
            />
          )}
        {/* Search */}
        {components.includes(KHeaderWidgetComponent.SearchBar) && (
          <KSearchInput
            query={query}
            setQuery={handleQueryUpdate}
            className={classes.searchBar}
            inputBorderRadius={
              configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsSearchBar?.inputBorderRadius
            }
            backgroundColor={
              context === KHeaderWidgetComponentContext.Burger
                ? "inherit"
                : convertColor(
                    configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsSearchBar
                      ?.backgroundColor,
                  )
            }
            textColor={
              context === KHeaderWidgetComponentContext.Burger
                ? theme.palette.text.primary
                : convertColor(
                    configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsSearchBar?.textColor,
                  )
            }
            label={t("search.header.search.input.label") as string}
            placeholder={t("search.header.description.search.placeholder.label") as string}
            buttonLabel=""
            size="small"
            onSearch={onSearch}
            onOptionSelected={selectOption}
            autoCompleteOptions={autoCompleteVals}
            loading={loading}
            splitValue={kSplitValue}
            splitLabel={t("search.header.description.search.split.label") as string}
            splitValues={kSplitValues}
            onSplitSelect={setKSplitValue}
            KIcon={KIcon}
          />
        )}
        {/* custom links */}
        {components.includes(KHeaderWidgetComponent.CustomNavigation) &&
          configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsCustomLinks?.map(
            (customLink, index) => (
              <CustomLink
                key={index}
                localeLinkWithIconSchema={customLink}
                color={
                  context === KHeaderWidgetComponentContext.Burger
                    ? theme.palette.text.primary
                    : convertColor(
                        configuration.kHeaderWidgetActions?.kHeaderWidgetActionsHome?.color,
                      )
                }
                className={classes.customLink}
              />
            ),
          )}
        {/* Home Button */}
        {components.includes(KHeaderWidgetComponent.Home) && (
          <Link href="/home" passHref legacyBehavior>
            <KButton
              tabIndex={-1}
              aria-hidden
              component="a"
              className={clsx(classes.button, classes.buttonHome)}
              style={{
                color:
                  context === KHeaderWidgetComponentContext.Burger
                    ? theme.palette.text.primary
                    : convertColor(
                        configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsHome?.color,
                      ),
              }}
            >
              {context === KHeaderWidgetComponentContext.Burger && (
                <KIcon className={`${classes.iconHome} fa-solid fa-house`} />
              )}
              <Typography
                variant="button"
                style={{
                  textTransform: "none",
                }}
              >
                <EditableSanitizeHtml labelKey={"layout.app-bar.homepage-button.label"} />
              </Typography>
            </KButton>
          </Link>
        )}
        {/* User */}
        {components.includes(KHeaderWidgetComponent.User) &&
          !conditionForConnectButton &&
          context !== KHeaderWidgetComponentContext.Burger &&
          !isEmptyMenu && (
            <CurrentUserMenu
              showUserName={
                configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsUser?.nameActive
              }
              color={convertColor(
                configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsUser?.color,
              )}
              cookieConsentManagement={displayCookieConsentManagement}
              className={classes.user}
            />
          )}
        {components.includes(KHeaderWidgetComponent.User) &&
          !conditionForConnectButton &&
          context !== KHeaderWidgetComponentContext.Burger &&
          isEmptyMenu && <Box sx={{ m: 1 }} />}
        {components.includes(KHeaderWidgetComponent.User) &&
          !conditionForConnectButton &&
          context === KHeaderWidgetComponentContext.Burger && (
            <>
              {configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsUser?.nameActive && (
                <Divider className={classes.user}>
                  <Typography
                    className={classes.userTitle}
                    variant="button"
                    style={{
                      color: theme.palette.text.primary,
                      textTransform: "none",
                    }}
                  >
                    {`${currentUser.currentUser.firstName ?? ""} ${
                      currentUser.currentUser.lastName ?? ""
                    }`}
                  </Typography>
                </Divider>
              )}
              <CurrentUserPopup
                onClose={function (): void {}}
                open={false}
                context={context}
                cookieConsentManagement={displayCookieConsentManagement}
              />
            </>
          )}
        {conditionForConnectButton && (
          <ButtonBase
            component="a"
            className={clsx(classes.button)}
            onClick={() => logout(`${location.origin}?${QUERY_BACKDOOR.STAY_ON_LANDING_AUTH}`)} // Log the user out and redirect them to the landing page with a query parameter indicating that they should stay logged out.
          >
            <Typography
              variant="button"
              style={{
                color: convertColor(
                  configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsHome?.color,
                ),
                textTransform: "none",
              }}
            >
              <EditableSanitizeHtml labelKey={"layout.login-button.label"} />
            </Typography>
          </ButtonBase>
        )}
        {/* Lang Selector */}
        {components.includes(KHeaderWidgetComponent.LangSelector) &&
          context !== KHeaderWidgetComponentContext.Burger && (
            <KLanguageSwitcher
              className={classes.langSelector}
              sx={getKLanguageSwitcherStyle(
                convertColor(
                  configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsLangSelector?.color,
                ),
              )}
            />
          )}
      </StyledKHeaderWidgetActionsBox>
    );
  };

export default KHeaderWidgetActionsBox;
